﻿import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";

import { hasPermission, toast, validateEmail, validRoles } from "@/services/Utils";
import api from "@/services/api.service";

import classes from "../../maintainers.module.scss";
import Fetching from "@/components/Fetching/Fetching";
import UsersTabs from "./UsersTabs";

import { base_user, customStyles } from "../users.constants";
import { ImageUpload } from "@/components/Form/ImageUpload/ImageUpload";
import { useSaveDocument } from "@/hooks/maintainers/users";
import { ReactComponent as ArrowUpSvg } from "@/assets/images/desktop/Flechaverdehorizontal.svg";
import { ReactComponent as ArrowDownSvg } from "@/assets/images/desktop/flechaverdeabajo.svg";

import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";

const UsersForm = ({ customers, confirmSave, permissions }) => {
	const { addToast } = useToasts();
	const location = useLocation();
	const { option, slug } = useParams();
	const navigate = useNavigate();

	const { base_permissions, estates, estates_parents, areas } = useSelector((state) => state.maintainers_users);
	const { user } = useSelector((state) => state.auth);

	const { customer } = user;

	const [isFetching, setIsFetching] = useState(true);
	const [data, setData] = useState("");
	const [files, setFiles] = useState([]);
	const [showSignature, setShowSignature] = useState(false);

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isAdmin = validRoles(userRoles, ["administrador"]);
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola", "administrador_campo"]);

	const { mutate: saveSignature, isLoading: savingSignature } = useSaveDocument();

	useEffect(() => {
		if (files.length === 1 && !data.signature_url) {
			(async () => {
				let newFiles = [];
				let validFiles = true;

				for (var i = 0; i < files.length; i++) {
					const base64_file = await new Promise((resolve, reject) => {
						if (files[i]) {
							const reader = new FileReader();
							reader.readAsDataURL(files[i]);
							reader.onload = () => resolve(reader.result);
							reader.onerror = (error) => reject(error);
						}
					});

					if (files[i].size / 1024 / 1024 < 5) {
						newFiles.push({
							id: "",
							name: files[i].name,
							url: base64_file,
							size: files[i].size,
							type: files[i].name.split(".").pop(),
							related_table: "application_orders_signatures",
						});
					} else {
						validFiles = false;
					}
				}

				if (!validFiles) {
					addToast("La imagen de la firma supera el peso máximo permitido (5MB).", {
						appearance: "error",
					});
				} else {
					saveSignature(
						{
							id: "",
							files: newFiles,
							pathname: location.pathname,
						},
						{
							onSuccess: (response) => {
								setData({ ...data, signature_url: response.url, signature_file_id: response.files[0]?.id });
								setShowSignature(true);
							},
						},
					);
				}
			})();
		}
		// eslint-disable-next-line
	}, [files]);

	useEffect(() => {
		setIsFetching(true);
		if (option && slug) {
			api.get("/admin/users/detail", { slug }).then((response) => {
				const { detail } = response;
				if (detail) {
					setData(detail);
					if (detail.signature_url) {
						setFiles([
							{
								preview: detail.signature_url,
							},
						]);
					}
				} else {
					addToast("Ocurrió un problema al cargar el usuario seleccionado.", {
						appearance: "error",
					});
					handleClose();
				}
				setIsFetching(false);
			});
		} else {
			let newBaseUser = { ...base_user };
			newBaseUser.customer_id = customer.id;
			setData({ ...newBaseUser, permissions: base_permissions });
			setIsFetching(false);
		}
		// eslint-disable-next-line
	}, [slug, option]);

	const handleClose = () => {
		emptyFields();
		setShowSignature(false);
		setFiles([]);

		if (location.pathname.includes("configuracion")) {
			navigate("/configuracion/usuarios");
			return;
		}

		if (location.pathname.includes("mantenedores")) {
			navigate("/mantenedores/usuarios");
			return;
		}

		if (location.pathname.includes("mis_campos")) {
			navigate("/mis_campos");
			return;
		}
	};

	const validSave = () => {
		if (!data.name) {
			toast(addToast, "Debe ingresar un nombre para guardar.", "error");
			return false;
		}

		if (!data.email) {
			toast(addToast, "Debe ingresar un email para guardar.", "error");
			return false;
		}

		if (!validateEmail(data.email)) {
			toast(addToast, "Debe ingresar un email válido guardar.", "error");
			return false;
		}

		let phoneToValidate = data.phone;
		if (phoneToValidate && !phoneToValidate.startsWith("+")) {
			phoneToValidate = "+" + phoneToValidate;
		}
		if (data.phone && isPossiblePhoneNumber(phoneToValidate) === false) {
			toast(addToast, "Debe ingresar un teléfono válido para guardar.", "error");
			return false;
		}

		if (!data.area_id) {
			toast(addToast, "Debe seleccionar un área para guardar.", "error");
			return false;
		}

		if (data.password) {
			if (data.password !== data.confirm_password) {
				addToast("Las contraseñas ingresadas no coinciden.", {
					appearance: "error",
				});
				return false;
			}

			if (data.password.length < 4) {
				addToast("La contraseña debe contener al menos 4 caracteres", {
					appearance: "error",
				});
				return false;
			}
		}

		if (!data.customer_id && location.pathname.includes("mantenedores")) {
			addToast("Debe seleccionar un cliente para guardar.", {
				appearance: "error",
			});

			return false;
		}

		return true;
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSave({ form: data }, setIsFetching, afterSave);
		}
	};

	const emptyFields = () => {
		let newBaseUser = { ...base_user };
		newBaseUser.customer_id = customer.id;
		setData({ ...newBaseUser });
	};

	const afterSave = () => {
		handleClose();
		addToast("Usuario guardado correctamente.", {
			appearance: "success",
		});
	};

	return (
		<Modal show={option ? true : false} onHide={handleClose} backdrop="static" keyboard={false} className={`no_print ${classes.main_form}`} size="lg">
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Usuario</h4>
			</Modal.Header>
			<Modal.Body>
				{isFetching ? (
					<Fetching topMargin={"1em"} />
				) : (
					<Row className={classes.row_form}>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Email</Form.Label>
								<Form.Control
									value={data.email}
									onChange={(e) =>
										setData({
											...data,
											email: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Nombre</Form.Label>
								<Form.Control
									value={data.name}
									onChange={(e) =>
										setData({
											...data,
											name: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Teléfono</Form.Label>
								<PhoneInput
									initialValueFormat="national"
									defaultCountry="CL"
									value={data && data.phone ? `+${data.phone.replace(/\+/g, "")}` : ""}
									onChange={(value) => {
										setData({
											...data,
											phone: value,
										});
									}}
									type="text"
								/>
							</Form.Group>
						</Col>

						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Área</Form.Label>
								<Select
									value={areas?.find((a) => a.value === data.area_id) || null}
									placeholder={<div>Seleccione</div>}
									options={areas}
									onChange={(value) =>
										setData({
											...data,
											area_id: value ? value.value : null,
										})
									}
									components={{
										NoOptionsMessage,
									}}
									styles={customStyles}
									isClearable
								/>
							</Form.Group>
						</Col>

						{isAdmin && (
							<>
								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Contraseña</Form.Label>
										<Form.Control
											value={data.password ? data.password : ""}
											onChange={(e) =>
												setData({
													...data,
													password: e.currentTarget.value,
												})
											}
											type="password"
											autoComplete="new-password"
										/>
									</Form.Group>
								</Col>

								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Confirmar Contraseña</Form.Label>
										<Form.Control
											value={data.confirm_password ? data.confirm_password : ""}
											onChange={(e) =>
												setData({
													...data,
													confirm_password: e.currentTarget.value,
												})
											}
											type="password"
										/>
									</Form.Group>
								</Col>
							</>
						)}

						{isManager && isAdmin && !location.pathname.includes("configuracion") && (
							<>
								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Cliente</Form.Label>
										<Select
											value={customers && customers.filter((c) => Number(c.value) === Number(data.customer_id))}
											placeholder={<div>Seleccione</div>}
											options={customers ? customers : []}
											onChange={(value) =>
												setData({
													...data,
													customer_id: value ? value.value : "",
													estates_parents: [],
													estates: [],
												})
											}
											components={{
												NoOptionsMessage,
											}}
											styles={customStyles}
											isClearable
										/>
									</Form.Group>
								</Col>
								<Col md={6}></Col>
							</>
						)}

						{isManager && (
							<>
								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>Acceso</Form.Label>
										<Form.Group className={classes.group_form}>
											<Col sm={6}>
												<Form.Check
													checked={data.access_system && Number(data.access_system) === 1 ? true : false}
													type="switch"
													id="data.access_system"
													label="Sistema"
													onChange={() =>
														setData({
															...data,
															access_system: !data.access_system,
														})
													}
												/>
											</Col>
											<Col sm={6}>
												<Form.Check
													checked={data.access_balance && Number(data.access_balance) === 1 ? true : false}
													type="switch"
													id="data.access_balance"
													label="Balanza"
													onChange={() =>
														setData({
															...data,
															access_balance: !data.access_balance,
														})
													}
												/>
											</Col>
											{user.superuser ? (
												<Col sm={6}>
													<Form.Check
														checked={data.access_api && Number(data.access_api) === 1 ? true : false}
														type="switch"
														id="data.access_api"
														label="API"
														onChange={() =>
															setData({
																...data,
																access_api: !data.access_api,
															})
														}
													/>
												</Col>
											) : (
												""
											)}
										</Form.Group>
									</Form.Group>
								</Col>

								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>
											<div
												style={{
													display: "flex",
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
													width: "100%",
													gap: "10px",
												}}
												onClick={() => setShowSignature(!showSignature)}
											>
												<div>Firma</div>
												{data.signature_url ? showSignature ? <ArrowUpSvg /> : <ArrowDownSvg /> : <></>}
											</div>
										</Form.Label>

										{savingSignature ? (
											<Fetching topMargin={"1em"} bottomMargin={"1em"} />
										) : (
											(!data.signature_url || showSignature) && (
												<ImageUpload
													data={data}
													setData={setData}
													files={files}
													setFiles={setFiles}
													returnFile={true}
													format=".png"
													text="Arrastre y suelte una imagen png aquí, o haga clic para seleccionar la imagen"
												/>
											)
										)}
									</Form.Group>
								</Col>

								{(!!customer.use_estates_parents || isAdmin) && (
									<Col md={12}>
										<Form.Group className={classes.group_form}>
											<Form.Label className={classes.label}>
												{customer.customer_screens.find((cs) => cs.screen_id === 38)
													? customer.customer_screens.find((cs) => cs.screen_id === 38)?.name
													: "Grupos Campos"}
											</Form.Label>
											<Select
												isMulti
												value={
													estates_parents &&
													estates_parents.filter(
														(estateParent) => data && data.estates_parents && data.estates_parents.includes(estateParent.value),
													)
												}
												placeholder={<div>Seleccione </div>}
												options={estates_parents.filter((et) => Number(et.customer_id) === Number(data.customer_id))}
												onChange={(value) => {
													let newEstatesParents = [];
													value &&
														value.map((estate) => {
															newEstatesParents.push(estate.value);
															return null;
														});

													setData({
														...data,
														estates_parents: newEstatesParents,
														estates: [],
													});
												}}
												components={{
													NoOptionsMessage,
												}}
												styles={customStyles}
											/>
										</Form.Group>
									</Col>
								)}

								<Col md={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>
											{customer.use_estates_parents && customer.customer_screens.find((cs) => cs.screen_id === 51)
												? customer.customer_screens.find((cs) => cs.screen_id === 51)?.name
												: "Campo"}
										</Form.Label>
										<Select
											isMulti
											value={estates && estates.filter((estate) => data && data.estates && data.estates.includes(estate.value))}
											placeholder={<div>Seleccione </div>}
											options={estates.filter((et) =>
												Number(et.customer_id) === Number(data.customer_id) && customer.use_estates_parents
													? data.estates_parents.includes(et.estate_parent_id)
													: Number(et.customer_id) === Number(data.customer_id),
											)}
											onChange={(value) => {
												let newEstates = [];
												value &&
													value.map((estate) => {
														newEstates.push(estate.value);
														return null;
													});
												setData({
													...data,
													estates: newEstates,
												});
											}}
											components={{
												NoOptionsMessage,
											}}
											styles={customStyles}
										/>
									</Form.Group>
								</Col>
							</>
						)}

						<UsersTabs data={data} setData={setData} />
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer>
				{(data.id ? hasPermission("edit", permissions) : hasPermission("create", permissions)) && !isFetching && (
					<Button variant="success" onClick={handleSave}>
						GUARDAR
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default UsersForm;
